/**
 * The copy of a background's position to other products (replicateBg.ts), as
 * one run: it reads what each target needs, writes the rows, then cuts the
 * images of the baked channels. The calls to the server are given by the
 * caller (ReplicateBgSection.tsx), so a test can run it without a server.
 *
 * The order is the order of a default save (bakeOnSave.ts): every row first,
 * then the cuts, because a cut takes seconds. A page left during the cuts
 * keeps the copied crops; a banner without a cut shows its crop.
 */
import {bakeRowPatch, isBakeableSource, runBakeJobs, type BakeRequest} from "./bakeOnSave";
import type {OverrideRow, RowSpecificity} from "./cropRowTarget";
import {
  findRow,
  replicaPatch,
  replicaRowAddress,
  replicaTemplateIds,
  replicaWrites,
  logoVariantFor,
  type ImageSize,
  type ReplicaColours,
  type ReplicaLook,
  type ReplicaSource,
  type SkippedTemplate,
} from "./replicateBg";

/** A row of the channel, as the API calls name it. */
export type ReplicaRowAddress = RowSpecificity & {product: string};

export interface ReplicaTarget {
  product: string;
  /** The identity of the matched background. */
  background: string;
  /** Its full-size image: the crop is in its pixels. */
  imageUrl: string;
}

export interface ReplicaApi {
  /** Natural sizes of images; null for an image that could not be read. */
  imageSizes: (urls: string[]) => Promise<Record<string, ImageSize | null>>;
  /**
   * The resolved default scene of a target route. `productDefaultShown`: the
   * route shows its products.js scene, not the cascade
   * (libs/authoritativeDefaultScene.ts).
   */
  resolvedScene: (
    target: ReplicaTarget
  ) => Promise<{scene: Record<string, unknown> | null; productDefaultShown: boolean}>;
  /**
   * Write whole field values to a row. `previous` is the row before, for a
   * writer that replaces the row (PUT).
   */
  writeRow: (
    address: ReplicaRowAddress,
    sceneData: Record<string, unknown>,
    previous: Record<string, unknown> | null
  ) => Promise<void>;
  bakeBackground: BakeRequest;
}

export interface ReplicaRunInput {
  channel: string;
  type: string;
  set: string | null | undefined;
  /** The layout on screen. */
  layout: string;
  overrides: OverrideRow[];
  /** See replicaRowAddress. */
  sourceLayout: string | null;
  /** The banners of the page that give the copy. */
  sources: Readonly<Record<string, ReplicaSource>>;
  /** The gallery image of the page: the source crops are in its pixels. */
  sourceImageUrl: string;
  look: ReplicaLook | null;
  /** The colours of the banners; null when the copy does not take them. */
  colours?: ReplicaColours | null;
  /**
   * The logo cycle of a product, and the logo its brand shows by default
   * (libs/logo.js). Asked for each target and for `sourceProduct`.
   */
  logoChoices?: (product: string) => {cycle: ReadonlyArray<string | null>; preferred: string | null};
  /** The product on screen: its brand tells a logo of its own from a chosen one. */
  sourceProduct?: string;
  previewTemplate: string | null;
  targets: readonly ReplicaTarget[];
  /** The banners the page of a target draws, with its resolved scene. */
  targetTemplateIds: (product: string, scene: Record<string, unknown> | null) => string[];
  /** Cut the images of the copied banners (the baked channels). */
  bake: boolean;
  api: ReplicaApi;
  onProgress?: (progress: {phase: "writing" | "cutting"; done: number; total: number}) => void;
}

export interface ReplicaProductResult {
  product: string;
  background: string;
  /** The banners that got the copy. */
  templates: string[];
  skipped: SkippedTemplate[];
  /** Banners whose cut failed: they show the crop. */
  unbaked: string[];
  /** Why nothing was written for the product. */
  error: string | null;
}

/** A row as it was before the run: null when the run made it. */
export interface ReplicaUndoEntry {
  address: ReplicaRowAddress;
  previous: Record<string, unknown> | null;
}

export interface ReplicaRunResult {
  results: ReplicaProductResult[];
  undo: ReplicaUndoEntry[];
}

const addressKey = (address: ReplicaRowAddress) =>
  [address.product, address.layout || "", address.background || "", address.variant || ""].join(":");

const messageOf = (error: unknown) => (error instanceof Error ? error.message : String(error));

export async function runReplica(input: ReplicaRunInput): Promise<ReplicaRunResult> {
  const {channel, type, set, layout, overrides, sources, api, onProgress} = input;
  const sourceIds = Object.keys(sources);
  if (sourceIds.length === 0) throw new Error("The page has no positioned banner to copy");

  const sizes = await api.imageSizes([
    ...new Set([input.sourceImageUrl, ...input.targets.map((target) => target.imageUrl)]),
  ]);
  const sourceSize = sizes[input.sourceImageUrl];
  if (!sourceSize) throw new Error(`Could not read the size of ${input.sourceImageUrl}`);

  const undo = new Map<string, ReplicaUndoEntry>();
  const remember = (address: ReplicaRowAddress, previous: Record<string, unknown> | null) => {
    const key = addressKey(address);
    if (!undo.has(key)) undo.set(key, {address, previous});
  };

  interface Written {
    result: ReplicaProductResult;
    target: ReplicaTarget;
    address: ReplicaRowAddress;
    /** The row after the writes of the run. */
    row: Record<string, unknown>;
    transforms: Record<string, ReturnType<typeof replicaPatch>["transforms"][string]>;
  }
  const written: Written[] = [];
  const results: ReplicaProductResult[] = [];

  let done = 0;
  for (const target of input.targets) {
    onProgress?.({phase: "writing", done, total: input.targets.length});
    const result: ReplicaProductResult = {
      product: target.product,
      background: target.background,
      templates: [],
      skipped: [],
      unbaked: [],
      error: null,
    };
    results.push(result);
    try {
      const {scene, productDefaultShown} = await api.resolvedScene(target);
      // The products.js scene and the cascade are exclusive: a first row for
      // this background would replace the whole products.js scene of the
      // page (texts, colours), not only its crop.
      if (productDefaultShown) {
        throw new Error(
          "The page shows its products.js scene: save a default there first, then copy"
        );
      }
      const {ids, skipped} = replicaTemplateIds({
        sourceIds,
        targetIds: input.targetTemplateIds(target.product, scene),
        targetScene: scene as {customBgs?: Record<string, unknown>} | null,
      });
      result.skipped = skipped;
      if (ids.length === 0) throw new Error("No banner of the page can take the position");
      const targetSize = sizes[target.imageUrl];
      if (!targetSize) throw new Error(`Could not read the size of ${target.imageUrl}`);

      const rowAddress = replicaRowAddress({
        overrides,
        channel,
        product: target.product,
        type,
        set,
        layout,
        background: target.background,
        sourceLayout: input.sourceLayout,
      });
      const previous =
        findRow(overrides, {channel, type, set, product: target.product, address: rowAddress})
          ?.scene_data ?? null;
      const patch = replicaPatch({
        previous,
        templateIds: ids,
        sources,
        sourceSize,
        targetSize,
        look: input.look,
        previewTemplate: input.previewTemplate,
        colours: input.colours ?? null,
        targetScene: scene,
        logoFor: (logo) => {
          if (!input.logoChoices) return logo;
          const sourcePreferred = input.sourceProduct
            ? input.logoChoices(input.sourceProduct).preferred
            : null;
          return logoVariantFor(logo, input.logoChoices(target.product), sourcePreferred);
        },
      });
      const writes = replicaWrites({
        overrides,
        channel,
        product: target.product,
        type,
        set,
        layout,
        address: rowAddress,
        sceneData: patch.sceneData,
      });
      for (const write of writes) {
        remember(write.address, write.previous);
        await api.writeRow(write.address, write.sceneData, write.previous);
      }
      result.templates = ids;
      const address = {product: target.product, ...rowAddress};
      written.push({
        result,
        target,
        address,
        row: {...(previous ?? {}), ...patch.sceneData},
        transforms: patch.transforms,
      });
    } catch (error) {
      result.error = messageOf(error);
    }
    done++;
  }
  onProgress?.({phase: "writing", done, total: input.targets.length});

  if (input.bake && isBakeableSource(input.sourceImageUrl)) {
    const plans = written
      .filter(({target}) => isBakeableSource(target.imageUrl))
      .map((entry) => ({
        entry,
        jobs: entry.result.templates.map((id) => ({
          id,
          src: entry.target.imageUrl,
          width: sources[id]!.width,
          height: sources[id]!.height,
          transform: entry.transforms[id]!,
        })),
      }));
    const total = plans.reduce((sum, plan) => sum + plan.jobs.length, 0);
    let cut = 0;
    onProgress?.({phase: "cutting", done: cut, total});
    const counted: BakeRequest = (source) =>
      api.bakeBackground(source).finally(() => {
        cut++;
        onProgress?.({phase: "cutting", done: cut, total});
      });
    // One product after another: a page left during the cuts keeps the cuts
    // of the products that are done.
    for (const {entry, jobs} of plans) {
      const run = await runBakeJobs(jobs, counted);
      entry.result.unbaked = run.failed;
      if (Object.keys(run.results).length === 0) continue;
      try {
        await api.writeRow(entry.address, bakeRowPatch(entry.row, run.results), entry.row);
      } catch (error) {
        entry.result.unbaked = entry.result.templates;
        console.warn(`[replicaRun] Writing the cuts of ${entry.address.product} failed:`, error);
      }
    }
  }

  return {results, undo: [...undo.values()]};
}

/**
 * Put back the rows a run wrote: the old scene of a row that was there, no
 * row where the run made one. Rows go back in the reverse order.
 */
export async function undoReplica(
  entries: readonly ReplicaUndoEntry[],
  api: {
    restoreRow: (address: ReplicaRowAddress, sceneData: Record<string, unknown>) => Promise<void>;
    deleteRow: (address: ReplicaRowAddress) => Promise<void>;
  }
): Promise<void> {
  for (const {address, previous} of [...entries].reverse()) {
    if (previous) await api.restoreRow(address, previous);
    else await api.deleteRow(address);
  }
}
